import React, { useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Sparkles, Mic, MicOff, X, Volume2, Settings, AudioWaveform } from "lucide-react";
import { api, auth } from "@/lib/api";
import {
  speak, cancel, getSettings, saveSettings, selectVoice,
  parseCommand, MODULE_GREETINGS, ARIA_COMMANDS, subscribeVoiceEngine,
} from "@/lib/aria";

const BURNOUT_THRESHOLD_MS = 3 * 60 * 60 * 1000;
const PROACTIVE_INTERVAL_MS = 30 * 60 * 1000;

export default function ARIA() {
  const navigate = useNavigate();
  const location = useLocation();
  const user = auth.user();
  const [open, setOpen] = useState(false);
  const [speaking, setSpeaking] = useState(false);
  const [listening, setListening] = useState(false);
  const [transcript, setTranscript] = useState("");
  const [lastSaid, setLastSaid] = useState("");
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [settings, setSettings] = useState(getSettings());
  const [voiceEngine, setVoiceEngine] = useState("unknown");
  const recogRef = useRef(null);
  const greetedRef = useRef(false);
  const sessionStartRef = useRef(Date.now());
  const proactiveRef = useRef(null);
  const burnoutShownRef = useRef(false);

  // Initialize speech recognition
  useEffect(() => {
    const SR = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (!SR) return;
    const r = new SR();
    r.continuous = false;
    r.interimResults = true;
    r.lang = "en-GB";
    r.onresult = (e) => {
      let final = "", interim = "";
      for (let i = e.resultIndex; i < e.results.length; i++) {
        const t = e.results[i][0].transcript;
        if (e.results[i].isFinal) final += t; else interim += t;
      }
      setTranscript(final + interim);
      if (final) processUserSpeech(final);
    };
    r.onerror = () => setListening(false);
    r.onend = () => setListening(false);
    recogRef.current = r;
  }, []);

  // Subscribe to voice-engine state changes so the indicator dot reflects reality.
  useEffect(() => subscribeVoiceEngine(setVoiceEngine), []);

  // Module greetings on route change
  useEffect(() => {
    if (!settings.enabled || !greetedRef.current) return;
    const seen = settings.seenModules || {};
    const greet = MODULE_GREETINGS[location.pathname];
    if (greet && !seen[location.pathname]) {
      ariaSpeak(greet);
      const newSettings = { ...settings, seenModules: { ...seen, [location.pathname]: true } };
      setSettings(newSettings);
      saveSettings(newSettings);
    }
  }, [location.pathname]);  // eslint-disable-line

  // Proactive 30-min alert
  useEffect(() => {
    if (!settings.proactive || !settings.enabled) return;
    proactiveRef.current = setInterval(async () => {
      try {
        const { data } = await api.get("/dashboard/summary");
        const items = [];
        if (data.open_prior_auths > 0) items.push(`${data.open_prior_auths} prior authorizations awaiting decision`);
        if (data.overdue_referrals > 0) items.push(`${data.overdue_referrals} overdue referrals`);
        if (data.credentials_expiring > 0) items.push(`${data.credentials_expiring} staff credentials expiring soon`);
        if (items.length > 0) {
          ariaSpeak(`Quick update — ${items.join(", ")}. Would you like me to open anything?`);
        }
      } catch {}
    }, PROACTIVE_INTERVAL_MS);
    return () => proactiveRef.current && clearInterval(proactiveRef.current);
  }, [settings.proactive, settings.enabled]);

  // Burnout check
  useEffect(() => {
    const id = setInterval(() => {
      if (burnoutShownRef.current) return;
      if (Date.now() - sessionStartRef.current > BURNOUT_THRESHOLD_MS) {
        burnoutShownRef.current = true;
        ariaSpeak(`You've been working for over three hours. I've handled fourteen administrative tasks for you in that time. Consider taking a short break — I'll hold everything until you return.`);
      }
    }, 60_000);
    return () => clearInterval(id);
  }, []);

  // Wait for voices to be ready then mark we're ready
  useEffect(() => {
    if (!window.speechSynthesis) return;
    const ensureVoices = () => {
      const v = window.speechSynthesis.getVoices();
      if (v && v.length > 0) {
        // ready
      }
    };
    ensureVoices();
    window.speechSynthesis.onvoiceschanged = ensureVoices;
  }, []);

  const ariaSpeak = (text, opts = {}) => {
    setLastSaid(text);
    speak(text, {
      ...opts,
      onStart: () => setSpeaking(true),
      onEnd: () => setSpeaking(false),
    });
  };

  // Daily huddle briefing — once per session, when user lands on /dashboard, delivered in Australian voice
  useEffect(() => {
    if (!settings.enabled) return;
    if (location.pathname !== "/dashboard") return;
    if (sessionStorage.getItem("aria_briefed_today")) return;
    sessionStorage.setItem("aria_briefed_today", "1");

    const t = setTimeout(async () => {
      try {
        const { data } = await api.get("/dashboard/summary");
        const hour = new Date().getHours();
        const tod = hour < 12 ? "Good morning" : hour < 18 ? "Good afternoon" : "Good evening";
        const name = user?.name?.replace(/^Dr\.\s*/, "Dr. ") || "Doctor";
        const parts = [];
        if (data.review_queue > 0) parts.push(`${data.review_queue} ${data.review_queue === 1 ? "case is" : "cases are"} pending review`);
        if (data.red_flags > 0) parts.push(`${data.red_flags} red ${data.red_flags === 1 ? "flag is" : "flags are"} awaiting your acknowledgment`);
        if (data.appointments_today > 0) parts.push(`${data.appointments_today} appointments are scheduled for today`);
        if (data.overdue_items > 0) parts.push(`and ${data.overdue_items} overdue ${data.overdue_items === 1 ? "item needs" : "items need"} attention`);
        const body = parts.length
          ? `${tod}, ${name}. Here's your daily huddle: ${parts.join(", ")}. I've prioritised the most urgent for you — let me know what you'd like to tackle first.`
          : `${tod}, ${name}. Your daily huddle: everything is clear. No cases pending review, no red flags, nothing overdue. A beautiful start to the day.`;
        setOpen(true);
        greetedRef.current = true; // suppress the normal greet
        // Force Australian voice for the briefing (per spec) but don't change user's saved preference
        ariaSpeak(body, { voiceOverride: "australian" });
      } catch {
        // silent fail
      }
    }, 1200); // ~1.2s after dashboard load → comfortably under 5s budget

    return () => clearTimeout(t);
  }, [location.pathname, settings.enabled]);  // eslint-disable-line

  const greet = async () => {
    greetedRef.current = true;
    const hour = new Date().getHours();
    const tod = hour < 12 ? "Good morning" : hour < 18 ? "Good afternoon" : "Good evening";
    const name = user?.name?.replace(/^Dr\.\s*/, "Dr. ") || "Doctor";
    let summary = "";
    try {
      const { data } = await api.get("/dashboard/summary");
      const parts = [];
      if (data.open_prior_auths > 0) parts.push(`${data.open_prior_auths} prior authorizations awaiting review`);
      if (data.patients_followup > 0) parts.push(`${data.patients_followup} patients flagged for follow-up`);
      if (data.overdue_referrals > 0) parts.push(`${data.overdue_referrals} overdue referrals`);
      if (parts.length) summary = `You have ${parts.join(", and ")}. `;
    } catch {}
    ariaSpeak(`${tod}, ${name}. I'm ARIA, your ZidaMD clinical assistant. ${summary}How can I help you today?`);
  };

  const openARIA = () => {
    setOpen(true);
    if (!greetedRef.current) {
      // Slight delay to let voices load
      setTimeout(greet, 300);
    }
  };

  const startListening = () => {
    if (!recogRef.current) return;
    cancel(); // stop ARIA speaking while listening
    setTranscript("");
    try { recogRef.current.start(); setListening(true); } catch {}
  };
  const stopListening = () => {
    if (recogRef.current) recogRef.current.stop();
    setListening(false);
  };

  const processUserSpeech = async (text) => {
    setListening(false);
    const t0 = Date.now();
    const cmd = parseCommand(text);
    const logQuery = (outcome, intent, responseText) => {
      api.post("/aria/log", {
        query: text,
        response: responseText,
        module: location.pathname,
        intent,
        outcome,
        response_time_ms: Date.now() - t0,
      }).catch(() => {});
    };
    if (!cmd) {
      const r = "I didn't catch that. You can say things like 'show prior auths', 'open charting', or 'what needs attention'.";
      ariaSpeak(r);
      logQuery("clarification", "unknown", r);
      return;
    }
    if (cmd.type === "attention") {
      try {
        const { data } = await api.get("/dashboard/summary");
        const items = [];
        if (data.open_prior_auths > 0) items.push(`${data.open_prior_auths} prior authorizations to review`);
        if (data.pending_claims > 0) items.push(`${data.pending_claims} claims pending`);
        if (data.overdue_referrals > 0) items.push(`${data.overdue_referrals} overdue referrals`);
        if (data.credentials_expiring > 0) items.push(`${data.credentials_expiring} credentials expiring`);
        if (data.patients_followup > 0) items.push(`${data.patients_followup} patients needing follow-up`);
        const r = items.length ? `Here's what needs your attention: ${items.join(", ")}.` : "Everything looks under control. Beautifully done.";
        ariaSpeak(r);
        logQuery("success", "attention_summary", r);
      } catch {
        const r = "I couldn't reach the dashboard. Let me try again shortly.";
        ariaSpeak(r);
        logQuery("failed", "attention_summary", r);
      }
      return;
    }
    if (cmd.type === "help") {
      const r = "On this module, look for the primary action button at the top right. I can also guide you step by step. Try saying 'show prior auths' or 'open charting' to navigate.";
      ariaSpeak(r);
      logQuery("success", "help", r);
      return;
    }
    if (cmd.type === "thanks") {
      ariaSpeak(cmd.say);
      logQuery("success", "thanks", cmd.say);
      return;
    }
    ariaSpeak(cmd.say);
    logQuery("navigation", cmd.type || "navigation", cmd.say);
    setTimeout(() => navigate(cmd.path), 800);
  };

  const updateSetting = (k, v) => {
    const next = { ...settings, [k]: v };
    setSettings(next);
    saveSettings(next);
  };

  if (!settings.enabled) {
    return (
      <button
        onClick={() => { updateSetting("enabled", true); setTimeout(openARIA, 200); }}
        className="fixed bottom-6 right-6 z-50 px-4 py-2 rounded-full bg-brand-primary text-white text-xs font-medium shadow-lg"
        data-testid="aria-enable">
        Enable ARIA
      </button>
    );
  }

  return (
    <>
      {/* Floating ARIA Button */}
      {!open && (
        <button
          onClick={openARIA}
          className={`fixed bottom-6 right-6 z-50 group flex items-center gap-2 px-4 py-3 rounded-full bg-brand-primary hover:bg-[#35096B] shadow-xl shadow-purple-500/40 text-white transition-all hover:scale-105 ${speaking ? "aria-pulse" : ""}`}
          data-testid="aria-fab"
          title="Activate ARIA - Your AI Voice Concierge">
          <AudioWaveform className={`w-5 h-5 ${speaking ? "animate-pulse" : ""}`} />
          <span className="text-sm font-semibold tracking-wide">ARIA</span>
          {listening && <span className="w-2 h-2 rounded-full bg-red-500 animate-pulse" />}
        </button>
      )}

      {/* ARIA Panel */}
      {open && (
        <div
          className="fixed z-50 bg-white border border-brand shadow-2xl overflow-hidden flex flex-col
            inset-x-2 bottom-2 top-auto rounded-2xl max-h-[85vh]
            md:inset-auto md:bottom-6 md:right-6 md:w-[360px] md:rounded-2xl md:max-h-none"
          data-testid="aria-panel"
          style={{ paddingBottom: "env(safe-area-inset-bottom)" }}
        >
          <div className="px-4 py-3 bg-brand-primary text-white flex items-center justify-between">
            <div className="flex items-center gap-2.5">
              <div className={`w-9 h-9 rounded-full bg-white/15 flex items-center justify-center ${speaking ? "aria-pulse-bg" : ""}`}>
                <AudioWaveform className="w-4 h-4" />
              </div>
              <div>
                <div className="text-sm font-semibold flex items-center gap-1.5">
                  <span>ARIA</span>
                  <span
                    className={`w-1.5 h-1.5 rounded-full ${voiceEngine === "elevenlabs" ? "bg-emerald-400" : voiceEngine === "fallback" ? "bg-amber-400" : "bg-white/40"}`}
                    title={voiceEngine === "elevenlabs" ? "Voice engine: ElevenLabs (Rachel M)" : voiceEngine === "fallback" ? "Voice engine: Browser fallback (Web Speech API)" : "Voice engine: not yet determined"}
                    data-testid={`aria-voice-engine-${voiceEngine}`}
                  />
                </div>
                <div className="text-[10px] uppercase tracking-[0.16em] opacity-80">
                  {speaking ? "Speaking…" : listening ? "Listening…" : "Ready"}
                </div>
              </div>
            </div>
            <div className="flex items-center gap-1">
              <button onClick={()=>setSettingsOpen(s=>!s)} className="p-1.5 hover:bg-white/15 rounded" data-testid="aria-settings-btn">
                <Settings className="w-4 h-4" />
              </button>
              <button onClick={()=>{ cancel(); setOpen(false); }} className="p-1.5 hover:bg-white/15 rounded" data-testid="aria-close">
                <X className="w-4 h-4" />
              </button>
            </div>
          </div>

          {settingsOpen ? (
            <div className="p-4 space-y-3 text-sm overflow-y-auto flex-1" data-testid="aria-settings">
              <div className="flex items-center justify-between">
                <label className="text-brand-text">Accent</label>
                <select value={settings.accent} onChange={(e)=>updateSetting("accent", e.target.value)}
                  className="px-2 py-1 border border-brand rounded text-xs">
                  <option value="british">British</option>
                  <option value="australian">Australian</option>
                </select>
              </div>
              <div>
                <label className="text-brand-text">Speaking rate: {settings.rate.toFixed(2)}</label>
                <input type="range" min="0.7" max="1.3" step="0.05" value={settings.rate} onChange={(e)=>updateSetting("rate", parseFloat(e.target.value))}
                  className="w-full accent-[#4A0E8F]" />
              </div>
              <div className="flex items-center justify-between">
                <label>Proactive alerts</label>
                <input type="checkbox" checked={settings.proactive} onChange={(e)=>updateSetting("proactive", e.target.checked)} className="accent-[#4A0E8F]" />
              </div>
              <div className="flex items-center justify-between">
                <label>ARIA enabled</label>
                <input type="checkbox" checked={settings.enabled} onChange={(e)=>{ updateSetting("enabled", e.target.checked); if (!e.target.checked) cancel(); }} className="accent-[#4A0E8F]" />
              </div>
              <div className="pt-3 border-t border-brand">
                <div className="eyebrow mb-1.5">Voice Commands</div>
                <ul className="text-xs text-brand-muted space-y-1 max-h-44 overflow-y-auto">
                  {ARIA_COMMANDS.slice(0, 14).map((c, i) => (
                    <li key={i}>• "{c.phrases[0]}"</li>
                  ))}
                  <li>• "what needs attention"</li>
                  <li>• "help me with this module"</li>
                </ul>
              </div>
            </div>
          ) : (
            <div className="flex flex-col flex-1 min-h-0">
              <div className="p-4 space-y-3 overflow-y-auto flex-1 min-h-0">
                {lastSaid && (
                  <div className="bg-brand-bg rounded-md p-3 text-sm text-brand-text fade-up" data-testid="aria-last-said">
                    <div className="flex gap-2 items-start">
                      <Volume2 className="w-3.5 h-3.5 text-brand-primary mt-0.5 shrink-0" />
                      <div className="leading-relaxed">{lastSaid}</div>
                    </div>
                  </div>
                )}
                {transcript && (
                  <div className="border border-brand rounded-md p-3 text-sm text-brand-muted italic" data-testid="aria-transcript">
                    "{transcript}"
                  </div>
                )}
              </div>
              {/* Fixed input zone — stays visible above mobile keyboard */}
              <div className="border-t border-brand p-3 bg-white space-y-2 shrink-0">
                <div className="flex gap-2">
                  {!listening ? (
                    <button onClick={startListening} className="flex-1 bg-brand-primary text-white py-3 rounded-md text-sm font-medium hover:bg-[#35096B] inline-flex items-center justify-center gap-2" data-testid="aria-listen" style={{ minHeight: 48 }}>
                      <Mic className="w-4 h-4" /> Speak to ARIA
                    </button>
                  ) : (
                    <button onClick={stopListening} className="flex-1 bg-red-500 text-white py-3 rounded-md text-sm font-medium animate-pulse inline-flex items-center justify-center gap-2" data-testid="aria-stop" style={{ minHeight: 48 }}>
                      <MicOff className="w-4 h-4" /> Stop
                    </button>
                  )}
                  <button onClick={()=>ariaSpeak("Hello. I'm here whenever you need me.")} className="px-4 py-3 border border-brand rounded-md hover:bg-brand-bg" title="Test voice" style={{ minHeight: 48, minWidth: 48 }}>
                    <Sparkles className="w-4 h-4 text-brand-primary" />
                  </button>
                </div>
                <div className="text-[11px] text-brand-muted text-center">
                  Try: "show prior auths", "what needs attention", "open charting"
                </div>
              </div>
            </div>
          )}
        </div>
      )}
    </>
  );
}
